import React, { useMemo } from 'react';

import { Button } from '@components/Button';
import { Container } from '@components/Container';
import { ButtonInput, LogForm } from '@components/Form';
import { Layout } from '@components/Layout';
import { SpinnerModal } from '@components/SpinnerModal';
import { Stepper } from '@components/Stepper';
import { GoodsInput, YesNoInput } from '@components/utilities/MembershipForm/Form';
import { Reference } from '@components/utilities/MembershipForm/Reference';
import { Split } from '@components/utilities/MembershipForm/Split';
import { steps } from '@data/membershipForm';
import { usePatchChecklist } from '@hooks/mutation/usePatchChecklist';
import { useChecklist } from '@hooks/queries/useChecklist';
import { MembershipFormFields } from '@models/MembershipForm';
import { useButtonStyles, useEnabledSteps } from '@pages/utilities/MembershipForm/helpers';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createValidate, formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import { Form, Formik } from 'formik';
import { isRight } from 'fp-ts/lib/Either';
import { Prompt, useHistory } from 'react-router-dom';

const strings = createStrings({
  ja: {
    title: '確認事項',
    prev: '戻る',
    informationInputPrompt: '情報を入力してください。',
    realTimeAccountReference: () => (
      <>
        リアルタイム口座の取得は下記URLをご参考ください
        <br />
        <br />
        <a
          href="https://bbs.naccscenter.com/naccs/dfw/web/tetsuduki/realtime/"
          target="_blank"
          rel="noreferrer"
        >
          初めてのコード登録｜国連CEFACT日本事務局・日本輸出入者標準コード管理センター・貿易手続調査研究
          (jastpro.org)
        </a>
      </>
    ),
    exporterNumberReference: () => (
      <>
        輸出入者符号の取得は下記URLをご参照ください
        <br />
        <br />
        <a href="https://www.jastpro.org/pages/26/" target="_blank" rel="noreferrer">
          初めてのコード登録｜国連CEFACT日本事務局・日本輸出入者標準コード管理センター・貿易手続調査研究
          (jastpro.org)
        </a>
      </>
    ),
    legalReference: () => (
      <>
        関税法第69条の2第1項（輸出してはならない貨物）
        <br />
        <br />
        麻薬・向精神薬・大麻・覚せい剤等、児童ポルノ、
        特許権・実用新案権・意匠権・商標権・著作権の侵害物品
      </>
    ),
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  informationInputPrompt: {
    ...common.middleText,
    color: theme.palette.grey['500'],
    padding: '40px 0',
  },
  sectionContainer: {
    ...common.flexColumn,
    ...common.verticalSpace2,
    padding: '3rem 0',
  },
}));

const schema = MembershipFormFields.shared.toChecklistFormSchema();

const validate = createValidate(schema);

export const Checklist: React.FC = () => {
  const checklist = useChecklist();
  const patchChecklist = usePatchChecklist();
  const enabledSteps = useEnabledSteps();

  const history = useHistory();

  const classes = {
    ...useCommonStyles(),
    ...useButtonStyles(),
    ...useStyles(),
  };

  const initialValues = useMemo(
    () =>
      checklist.data && MembershipFormFields.shared.mapChecklistToChecklistForm(checklist.data!),
    [checklist.data],
  );

  return (
    <Layout loading={checklist.isLoading} title={strings.title} errors={[checklist.error]}>
      {() => (
        <Container>
          <SpinnerModal open={patchChecklist.isLoading} />
          <Formik
            validate={validate}
            initialValues={initialValues!}
            onSubmit={async (values) => {
              const e = schema.decode(values);
              if (isRight(e)) {
                await patchChecklist.mutateAsync(e.right);
                localStorage.removeItem('/utilities/MembershipForm/Checklist');
                history.push('/utilities/MembershipForm/DelegationForm');
                window.scrollTo(0, 0);
              } else {
                throw new Error('Checklist: Unexpected erroneus form values. ');
              }
            }}
          >
            {({ touched, isSubmitting }) => (
              <Form className={classes.flexColumn}>
                <Prompt
                  when={Object.keys(touched).length > 0 && !isSubmitting}
                  message={() => formStrings.promptLeaving}
                />

                <LogForm name="checklist" disabled />
                <Stepper steps={steps} value={steps[2].name} enabledSteps={enabledSteps} />
                <div className={classes.informationInputPrompt}>
                  {strings.informationInputPrompt}
                </div>
                <Split title={strings.title} />

                <div className={classes.sectionContainer}>
                  <YesNoInput field={MembershipFormFields.shared.question1} />
                  <YesNoInput field={MembershipFormFields.shared.question2} />
                  <Reference>{strings.realTimeAccountReference()}</Reference>
                  <YesNoInput field={MembershipFormFields.shared.question3} />
                  <YesNoInput field={MembershipFormFields.shared.question4} />
                  <YesNoInput field={MembershipFormFields.shared.question5} />
                  <YesNoInput field={MembershipFormFields.shared.question6} />
                  <Reference>{strings.exporterNumberReference()}</Reference>
                  <YesNoInput field={MembershipFormFields.shared.question7} />

                  <GoodsInput field={MembershipFormFields.shared.question8} type="export" />
                  <Reference>{strings.legalReference()}</Reference>
                  <GoodsInput field={MembershipFormFields.shared.question9} type="import" />
                  <Reference>{strings.legalReference()}</Reference>

                  <YesNoInput field={MembershipFormFields.shared.question10} />
                  <YesNoInput field={MembershipFormFields.shared.question11} />
                </div>

                <div className={classes.buttons}>
                  <Button
                    text={strings.prev}
                    color="primary"
                    className={clsx(classes.button, classes.prev)}
                    onClick={() => history.push('/utilities/MembershipForm/Information')}
                  />
                  <ButtonInput
                    text={formStrings.next}
                    color="primary"
                    className={clsx(classes.button, classes.next)}
                  />
                </div>
              </Form>
            )}
          </Formik>
        </Container>
      )}
    </Layout>
  );
};
